"use client";

import { useAuthActions, useConvexAuth } from "@convex-dev/auth/react";
import { useAction, useConvex, useMutation, useQuery, useQueries, type RequestForQueries } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { ArrowLeft, AtSign, Check as CheckIcon, X as XIcon } from "lucide-react";
import {
  ArrowRight,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  LogIn,
  Mail,
  UserPlus,
  UserRound,
} from "lucide-react";
import {
  Component,
  Fragment,
  type CSSProperties,
  type ErrorInfo,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import dynamic from "next/dynamic";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SanityMark } from "@/components/sanity-mark";
import { StudioSkeletonCard } from "@/components/studio-skeleton-chrome";
import { AUTH_CYCLE_MS, skeletonGreeting } from "@/mos-app/studio-boot-frame-script";
import { randomizeStudioAppearance } from "@/mos-app/theme.js";
import { playUiSound } from "@/mos-app/sounds.js";
import { nativeImpact } from "@/studio/lib/nativeShell";
import { StudioBootLoader } from "@/components/studio-boot-loader";
import {
  claimPaintBoot,
  dismissPaintBoot,
} from "@/components/studio-paint-boot-control";
import { api } from "../../../convex/_generated/api";
import { DisplayCurrencyProvider } from "@/studio/components/DisplayCurrencyProvider";
import { BillingCountrySelect } from "@/studio/components/BillingCountrySelect";
import {
  DEFAULT_MAIL_DOMAIN,
  normalizeSignupHandle,
  signupHandleDraft,
} from "../../../convex/lib/mail/addresses";
import { STUDIO_PLAN_CATALOG, STUDIO_PLAN_SLUGS } from "../../../convex/lib/studioPlans";
import { planPitch, quoteBillingPlan, StudioPlanCard, StudioChoosePlanHead, StudioPlanBarControls, StudioPlanCards } from "./billing/StudioPlanCards";
import { UnifiedCheckout } from "@/components/checkout/UnifiedCheckout";
import { planConfirmedDetail, topUpConfirmedDetail, useCheckoutPayment } from "@/components/checkout/useCheckoutPayment";
import { useCheckoutContactEmail, useCheckoutCurrency } from "@/components/checkout/useCheckoutCurrency";
import {
  playGrantMessage,
  playSubscriptionProduct,
  shouldUsePlayBilling,
  startPlayPurchase,
} from "@/studio/lib/playBillingClient";
import {
  canContinueSignup,
  passwordMismatchMessage,
  SIGNUP_HANDLE_DEBOUNCE_MS,
  SIGNUP_SCREENSHOT_EMAIL,
  SIGNUP_SCREENSHOT_NAME,
  signupHandleMessage,
  signupProgress,
  signupScreenshotPlan,
  signupPreviewHost,
  signupScreenshotStep,
  type SignupStep,
} from "./signupWizard";
import { friendlyConvexError } from "@/studio/lib/convexUserErrors";
import { stashWamReturn } from "@/studio/shell/studioWamReturn";
import { EmailFirstSignIn, signinScreenshotStage } from "./EmailFirstSignIn";
import {
  clearStoredConvexSession,
  hasStoredConvexSession,
  purgeStaleConvexSessionKeys,
} from "@/lib/studio-session-hint";
import { markPerfMilestone } from "@/lib/performance";
import {
  resetStudioClient,
  studioResetHref,
} from "@/studio/lib/studio-client-reset";
import { stripDocumentTabsFromOpenSession } from "@/studio/lib/studio-account-storage";
import {
  readStoredStudioDefaultTab,
  writeStoredStudioDefaultTab,
} from "@/studio/lib/studio-default-tab";
import { MobileBackStackHost } from "@/studio/components/MobileBackStackHost";
import { StudioHeadTabs } from "@/studio/components/chrome/StudioHeadTabs";
import {
  readStudioCustomCursorEnabled,
  useStudioCustomCursor,
} from "@/studio/hooks/useStudioCustomCursor";
// The auth sheet's CSS. This was a `styled-jsx global` block inside AuthFrame,
// which meant the sign-in card rendered UNSTYLED on the first paint — styled-jsx
// ships as JS, so it loses the race against hydration. It is now a real
// stylesheet, imported here AND inlined in the root layout head via
// getAuthSheetBootInlineCss(), so there is exactly one source of truth for the
// same rules and no flash of unstyled sign-in.
import { GoogleButton } from "@/app/sanity-web/ui";
import "./studio-auth-sheet.css";
import "./studio-desktop-shell.css";

type StudioShellBootProps = {
  initialProfileUsername?: string;
  onReady?: () => void;
};

/** A lone Convex query timeout (backend briefly busy) remounts the shell
 * quietly instead of walling it. More than this many in the window falls
 * back to the busy screen + reload. */
const QUIET_REMOUNT_MAX = 2;
const QUIET_REMOUNT_WINDOW_MS = 60_000;
const QUIET_REMOUNT_DELAY_MS = 700;
let quietRemountTimes: number[] = [];

function recentQuietRemounts(now = Date.now()) {
  quietRemountTimes = quietRemountTimes.filter((t) => now - t < QUIET_REMOUNT_WINDOW_MS);
  return quietRemountTimes.length;
}

function isConvexTimeoutMessage(message: string) {
  return /timed out|timeout|try again later/i.test(message);
}

function isUpdateDepthMessage(text: string): boolean {
  return /Minified React error #185|Maximum update depth exceeded/i.test(text);
}

class StudioShellErrorBoundary extends Component<
  { children: ReactNode; onFailed?: () => void },
  { failed: boolean; message: string; autoReload: boolean; quiet: boolean; generation: number }
> {
  state = { failed: false, message: "", autoReload: false, quiet: false, generation: 0 };
  private reloadTimer: ReturnType<typeof setTimeout> | null = null;
  private remountTimer: ReturnType<typeof setTimeout> | null = null;

  static getDerivedStateFromError(error: Error) {
    const message = error?.message
      ? String(error.message).slice(0, 280)
      : "Studio crashed while loading.";
    const blob = `${error?.name ?? ""} ${message}`;
    // A tab held open across a deploy still asks for its old lazy chunks
    // (editors, viewers). Those 404 on the new build — reload, don't wall.
    // Stale/corrupt chunks can also throw RangeError ("Invalid array length")
    // while evaluating — same recovery; drop sticky Script tabs first.
    const autoReload =
      /timed out|timeout|out_of_retention|try again later/i.test(message) ||
      /ChunkLoadError|Loading chunk|dynamically imported module|Importing a module script failed/i.test(
        blob,
      ) ||
      /Invalid array length|RangeError/i.test(blob);
    // React #185 (update-depth loop) is usually a one-off render race on
    // boot; a fresh remount of the shell clears it. Same quiet budget as
    // timeouts, so a loop that comes straight back still shows the wall.
    const quiet =
      (isConvexTimeoutMessage(message) || isUpdateDepthMessage(blob)) &&
      recentQuietRemounts() < QUIET_REMOUNT_MAX;
    return { failed: true, message, autoReload, quiet };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (this.state.quiet && isUpdateDepthMessage(`${error?.name ?? ""} ${error?.message ?? ""}`)) {
      // Still report it: we need the component stack to find the loop.
      void fetch("/api/client-error/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: `[quiet-remount] ${error?.message ?? ""}`,
          stack: error?.stack ?? "",
          componentStack: info.componentStack ?? "",
          route: window.location.href,
          userAgent: navigator.userAgent,
          build: process.env.NEXT_PUBLIC_DESK_BUILD ?? "",
        }),
        keepalive: true,
      }).catch(() => {});
    }
    if (this.state.quiet) {
      // Single timeout: log it and remount the shell after a short pause —
      // no overlay, no reload. Repeats inside the window take the loud path.
      console.warn("[studio-shell-timeout-remount]", error?.message ?? "");
      quietRemountTimes.push(Date.now());
      if (this.remountTimer) clearTimeout(this.remountTimer);
      this.remountTimer = setTimeout(() => {
        this.remountTimer = null;
        this.setState((prev) => ({
          failed: false,
          message: "",
          autoReload: false,
          quiet: false,
          generation: prev.generation + 1,
        }));
      }, QUIET_REMOUNT_DELAY_MS);
      return;
    }
    this.props.onFailed?.();
    const payload = {
      message: error.message,
      stack: error.stack ?? "",
      componentStack: info.componentStack ?? "",
      route: window.location.href,
      userAgent: navigator.userAgent,
      build: process.env.NEXT_PUBLIC_DESK_BUILD ?? "",
    };
    console.error("[studio-shell-error]", payload);
    try {
      (window as Window & { __STUDIO_LAST_ERROR__?: typeof payload }).__STUDIO_LAST_ERROR__ =
        payload;
    } catch {
      /* ignore */
    }
    void fetch("/api/client-error/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});

    // Transient Convex isolate timeouts / stale chunks / bad Script render
    // should not leave a sticky recovery wall.
    if (this.state.autoReload) {
      try {
        const key = "ys-shell-timeout-reload";
        const last = Number(sessionStorage.getItem(key) || "0");
        if (Date.now() - last > 20_000) {
          sessionStorage.setItem(key, String(Date.now()));
          if (/Invalid array length|RangeError/i.test(String(error?.message ?? ""))) {
            try {
              stripDocumentTabsFromOpenSession();
            } catch {
              /* ignore */
            }
          }
          this.reloadTimer = setTimeout(() => {
            window.location.reload();
          }, 900);
        }
      } catch {
        /* ignore */
      }
    }
  }

  componentWillUnmount() {
    if (this.reloadTimer) clearTimeout(this.reloadTimer);
    if (this.remountTimer) clearTimeout(this.remountTimer);
  }

  render() {
    if (!this.state.failed) {
      return <Fragment key={this.state.generation}>{this.props.children}</Fragment>;
    }
    if (this.state.quiet) return null;
    // Fixed overlay above PaintBoot / AuthGate boot (z-index 2147483000) so
    // Reset is always visible and clickable — previously the boot layer ate clicks.
    return (
      <div
        className="ys-boot-overlay"
        style={{ zIndex: 2147483001 }}
        data-ys-boot="recovery"
      >
        <StudioBootLoader
          recovery={
            <div className="mt-6 flex max-w-sm flex-col items-center gap-3 px-4 text-center">
              <p className="text-xs font-medium text-slate-900/70">
                {this.state.autoReload
                  ? "Studio backend was busy — reloading…"
                  : "Studio hit a load error and stopped here."}
              </p>
              {this.state.message ? (
                <p className="rounded-lg bg-slate-900/5 px-3 py-2 font-mono text-[11px] leading-snug text-slate-900/55 break-words">
                  {this.state.message}
                </p>
              ) : null}
              <p className="text-[11px] leading-snug text-slate-900/45">
                Tap Reset Studio to clear sticky tabs/cache, then reload. If this keeps happening, hard-refresh after reset.
              </p>
              <a
                href={studioResetHref()}
                className="rounded-xl border border-slate-900/15 px-4 py-2 text-xs font-semibold text-slate-900/70"
                onClick={(event) => {
                  event.preventDefault();
                  resetStudioClient("error-boundary");
                }}
              >
                Reset Studio
              </a>
            </div>
          }
        />
      </div>
    );
  }
}

/** Shell chunk loads under the static boot frame (StudioBootFrame); signals ready only after mount. */
const StudioShell = dynamic<StudioShellBootProps>(
  () =>
    import("./StudioShell").then((m) => {
      const Inner = m.StudioShell;
      return function StudioShellBootGate({ onReady, initialProfileUsername }: StudioShellBootProps) {
        useEffect(() => {
          onReady?.();
        }, [onReady]);
        return <Inner initialProfileUsername={initialProfileUsername} />;
      };
    }),
  {
    ssr: false,
    loading: () => null,
  },
);

type PendingSignup = {
  localPart: string;
  firstName: string;
  lastName: string;
  backupEmail?: string;
  /** From the sign-up wizard's email code. Marks the backup inbox verified. */
  emailProof?: string;
  auto?: boolean;
};

/** How long an "authenticated" client may wait on users.current before the
 * stored session is treated as dead and cleared (see deadSession). */
const USER_PENDING_GIVE_UP_MS = 15_000;

function isMissingAuthError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err);
  return /not authenticated/i.test(message);
}

async function retryWhileUnauthed<T>(run: () => Promise<T>): Promise<T> {
  const deadline = Date.now() + 10_000;
  for (;;) {
    try {
      return await run();
    } catch (err) {
      if (!isMissingAuthError(err) || Date.now() >= deadline) throw err;
      await new Promise((resolve) => window.setTimeout(resolve, 120));
    }
  }
}

export function StudioAuthGate({
  initialProfileUsername,
}: {
  initialProfileUsername?: string;
} = {}) {
  const auth = useConvexAuth();
  const { signOut: signOutStaleSession } = useAuthActions();
  // useQueries, not useQuery: a stored token the server rejects makes
  // users.current throw "Not authenticated", and useQuery rethrows that into
  // render — which took down the whole tree (blank page / error overlay)
  // instead of moving to sign-in. Here the error comes back as a value.
  // Memoised: useQueries resubscribes whenever the object identity changes.
  const isAuthed = Boolean(auth?.isAuthenticated);
  const currentUserQueries = useMemo((): RequestForQueries => {
    const queries: RequestForQueries = {};
    if (isAuthed) {
      queries.user = { query: api.users.current, args: {} };
      // Warm the main agent chat id while the boot frame shows, so the shell
      // lands on DMs with that chat open on its first frame
      // (useStudioMainAgentLanding). Result unused here; errors stay values.
      queries.mainAgent = { query: api.studioBots.agentSetupState, args: {} };
    }
    return queries;
  }, [isAuthed]);
  const currentUserResult = useQueries(currentUserQueries).user as
    | FunctionReturnType<typeof api.users.current>
    | Error
    | undefined;
  const currentUserError = currentUserResult instanceof Error ? currentUserResult : null;
  if (currentUserError && !isMissingAuthError(currentUserError)) {
    // Anything other than a dead session keeps the old behaviour (error boundary).
    throw currentUserError;
  }
  const currentUser = currentUserResult instanceof Error ? undefined : currentUserResult;
  // The token still looks valid locally, but the server has no user behind it:
  // the session was revoked (signed out elsewhere, or the in-app sign-out's
  // server call landed first) or the user row is gone. Either way the only way
  // forward is sign-in; this used to render nothing at all.
  //
  // Third case: the client believes it is authenticated but users.current never
  // answers. Seen with a stored refresh token the server throws on: Convex
  // reports "authenticated", the query never runs, and the boot frame sat there
  // for good. After USER_PENDING_GIVE_UP_MS that counts as dead too.
  const [userPendingGaveUp, setUserPendingGaveUp] = useState(false);
  const userStillPending =
    Boolean(auth?.isAuthenticated) && !auth?.isLoading && currentUserResult === undefined;
  useEffect(() => {
    if (!userStillPending) {
      setUserPendingGaveUp(false);
      return;
    }
    const timer = window.setTimeout(() => setUserPendingGaveUp(true), USER_PENDING_GIVE_UP_MS);
    return () => window.clearTimeout(timer);
  }, [userStillPending]);
  const deadSession =
    Boolean(auth?.isAuthenticated) &&
    (currentUserError !== null || currentUserResult === null || userPendingGaveUp);
  // Synchronous localStorage read (see studio-session-hint). Lets the gate draw
  // the real sign-in card on the first paint instead of waiting on a paint we
  // no longer have. Never used to grant access — only to choose what to draw.
  //
  // Seeded on the server as `null` and read in an effect, NOT via a useState
  // initializer: the server has no localStorage, so an initializer would make
  // the first client render disagree with the server HTML and throw a
  // hydration mismatch.
  const [hasSessionHint, setHasSessionHint] = useState<boolean | null>(null);
  const [authLoadTimedOut, setAuthLoadTimedOut] = useState(false);

  useEffect(() => {
    setHasSessionHint(hasStoredConvexSession());
  }, []);

  // Server says the stored session is dead. Give a just-issued token a moment
  // to be accepted (the same window retryWhileUnauthed covers), then sign out
  // locally: that clears the stored tokens, auth settles anonymous, and the
  // effect below draws sign-in. The boot frame stays up meanwhile, because
  // currentUser is still "loading".
  //
  // signOut() first asks the server, and with a broken session that call can
  // hang. If it has not finished a few seconds later, clear the stored tokens
  // directly and reload: the next load has no token, so it paints sign-in.
  useEffect(() => {
    if (!deadSession) return;
    let fallback = 0;
    const timer = window.setTimeout(() => {
      let finished = false;
      void Promise.resolve(signOutStaleSession())
        .catch(() => {})
        .finally(() => {
          finished = true;
        });
      fallback = window.setTimeout(() => {
        if (finished) return;
        clearStoredConvexSession();
        window.location.reload();
      }, 4000);
    }, 2500);
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(fallback);
    };
  }, [deadSession, signOutStaleSession]);

  // A stored token that the server rejects is cleared by Convex Auth, which
  // then settles as "not loading, not authenticated". The hint was read once
  // at mount and still says "signed in", so nothing drew sign-in and the page
  // sat blank behind the dismissed boot frame. Re-read storage whenever auth
  // settles anonymous so an expired or revoked session moves to sign-in.
  // Also drop token keys left under other namespaces, so nothing that scans
  // storage mistakes them for a live session on the next load.
  useEffect(() => {
    if (!auth || auth.isLoading || auth.isAuthenticated) return;
    purgeStaleConvexSessionKeys();
    setHasSessionHint(hasStoredConvexSession());
  }, [auth, auth?.isLoading, auth?.isAuthenticated]);
  const [shellReady, setShellReady] = useState(false);
  const [shellFailed, setShellFailed] = useState(false);
  const shellReadyRef = useRef(false);
  const paintBootClaimedRef = useRef(false);

  const markShellReady = useCallback(() => {
    if (shellReadyRef.current) return;
    shellReadyRef.current = true;
    setShellReady(true);
  }, []);

  const markShellFailed = useCallback(() => {
    setShellFailed(true);
  }, []);

  useEffect(() => {
    // Keep layout PaintBoot mounted — claim so it doesn't auto-dismiss.
    claimPaintBoot();
    paintBootClaimedRef.current = true;
  }, []);

  useEffect(() => {
    if (!auth?.isLoading && auth?.isAuthenticated) {
      markPerfMilestone("auth-ready");
    }
  }, [auth?.isAuthenticated, auth?.isLoading]);

  useEffect(() => {
    if (auth?.isLoading || !auth?.isAuthenticated) return;
    let back = "";
    try {
      back = sessionStorage.getItem("ys-mcp-oauth-return") ?? "";
      if (back) sessionStorage.removeItem("ys-mcp-oauth-return");
    } catch {
      return;
    }
    if (!back.startsWith(`${window.location.origin}/oauth/mcp`)) return;
    window.location.assign(back);
  }, [auth?.isAuthenticated, auth?.isLoading]);

  useEffect(() => {
    if (!auth?.isLoading) {
      setAuthLoadTimedOut(false);
      return;
    }
    // With a stored token a slow WebSocket confirmation should not bounce a
    // signed-in user to sign-in, so that case gets a much longer window. It
    // still needs one: a stored refresh token the server errors on (instead of
    // answering "signed out") leaves Convex Auth loading forever, which kept the
    // boot frame up with no way out. If auth does resolve later, the shell
    // replaces sign-in on its own.
    const timer = window.setTimeout(
      () => setAuthLoadTimedOut(true),
      hasSessionHint === true ? 12_000 : 3000,
    );
    return () => window.clearTimeout(timer);
  }, [auth?.isLoading, hasSessionHint]);

  useEffect(() => {
    // Only reset on sign-out / missing user — not while currentUser is still loading
    // (undefined), or the boot overlay can thrash as the shell mounts.
    if (!auth?.isAuthenticated || currentUser === null) {
      shellReadyRef.current = false;
      setShellReady(false);
      setShellFailed(false);
    }
  }, [auth?.isAuthenticated, currentUser]);

  const authPending = !auth || auth.isLoading;
  const userPending = Boolean(auth?.isAuthenticated) && currentUser === undefined;
  // Draw sign-in immediately in exactly one case: Convex has already said we
  // are anonymous AND there is no cached token. That is a certainty, so waiting
  // only delays the first paint.
  //
  // `hasSessionHint === null` means the client read has not landed yet — that is
  // the hydration-safe state and must NOT show sign-in, or every signed-in user
  // gets a sign-in flash before the shell.
  //
  // Once Convex Auth has SETTLED anonymous (not loading, not authenticated),
  // sign-in always renders, whatever the hint says. The hint only matters while
  // auth is still loading; letting it veto a settled answer is what left a
  // blank page after sign-out (hint still "signed in" → no sign-in, no shell,
  // boot frame dismissed).
  const definitelyAnonymous = auth?.isAuthenticated === false && hasSessionHint === false;
  const settledAnonymous = Boolean(auth) && !auth.isLoading && !auth.isAuthenticated;
  const showSignInScreen =
    Boolean(auth) &&
    !auth.isAuthenticated &&
    (settledAnonymous || definitelyAnonymous || authLoadTimedOut);
  const [awaitingBackupCode, setAwaitingBackupCode] = useState(false);
  const [signupProofPending, setSignupProofPending] = useState(false);
  const [signupBackup, setSignupBackup] = useState("");
  const [signupBackupError, setSignupBackupError] = useState("");
  // Null on the first paint. Reading the query in useState disagrees with the
  // server HTML whenever ?signupStep= is set.
  const [screenshotStep, setScreenshotStep] = useState<SignupStep | null>(null);
  const [signinPreview, setSigninPreview] = useState(false);
  useEffect(() => {
    setScreenshotStep(signupScreenshotStep(window.location.hostname, window.location.search));
    setSigninPreview(signinScreenshotStage(window.location.hostname, window.location.search) != null);
    // Preview-only test link: /?test=signup-plans opens step 4 with real data for a signed-in user.
    if (
      signupPreviewHost(window.location.hostname) &&
      new URLSearchParams(window.location.search).get("test") === "signup-plans"
    ) {
      setChoosePlan(true);
    }
  }, []);
  const [choosePlan, setChoosePlan] = useState(false);
  const completeSignup = useMutation(api.studioIdentity.completeSignup);
  const pendingSignupRef = useRef<PendingSignup | null>(null);
  const completingSignupRef = useRef(false);

  useEffect(() => {
    if (currentUser?.backupEmailVerified) {
      setAwaitingBackupCode(false);
      setSignupProofPending(false);
      setSignupBackup("");
      setSignupBackupError("");
    }
  }, [currentUser?.backupEmailVerified]);

  useEffect(() => {
    if (screenshotStep) return;
    const pending = pendingSignupRef.current;
    if (!pending || !auth?.isAuthenticated || auth.isLoading) return;
    // Password sign-up already stores the name, so a finished name is not proof the backup landed.
    const wantedBackup = pending.backupEmail?.trim().toLowerCase() ?? "";
    const backupLanded =
      !wantedBackup || currentUser?.backupEmail?.trim().toLowerCase() === wantedBackup;
    const identityDone =
      Boolean(currentUser?.firstName?.trim()) &&
      Boolean(currentUser?.lastName?.trim()) &&
      Boolean(currentUser?.email?.trim()) &&
      backupLanded;
    if (identityDone) {
      pendingSignupRef.current = null;
      completingSignupRef.current = false;
      return;
    }
    if (completingSignupRef.current) return;
    completingSignupRef.current = true;
    void retryWhileUnauthed(() => completeSignup(pending))
      .then(() => {
        pendingSignupRef.current = null;
      })
      .catch((err: unknown) => {
        completingSignupRef.current = false;
        setSignupProofPending(false);
        console.error("[studio-signup]", err);
        const raw = err instanceof Error ? err.message : String(err);
        const backupProblem = Boolean(wantedBackup) && /backup email/i.test(raw);
        const identityOnly = {
          localPart: pending.localPart,
          firstName: pending.firstName,
          lastName: pending.lastName,
          auto: pending.auto,
        };
        pendingSignupRef.current = backupProblem ? identityOnly : null;
        if (!backupProblem) setChoosePlan(false);
        const retryIdentity = backupProblem
          ? retryWhileUnauthed(() => completeSignup(identityOnly)).then(() => {
              pendingSignupRef.current = null;
            })
          : Promise.resolve();
        void retryIdentity
          .catch((retryErr: unknown) => {
            console.error("[studio-signup]", retryErr);
          })
          .finally(() => {
            setSignupBackup("");
            setSignupBackupError(friendlyConvexError(err, "Could not save your backup email"));
          });
      });
  }, [
    auth?.isAuthenticated,
    auth?.isLoading,
    completeSignup,
    currentUser?.backupEmail,
    currentUser?.email,
    currentUser?.firstName,
    currentUser?.lastName,
    screenshotStep,
  ]);

  const needsBackupEmail =
    Boolean(auth?.isAuthenticated) &&
    currentUser != null &&
    Boolean(currentUser.email?.trim()) &&
    Boolean(currentUser.firstName?.trim() && currentUser.lastName?.trim()) &&
    !currentUser.backupEmailVerified &&
    !signupProofPending &&
    !currentUser.phone?.trim();
  const showBackupEmail =
    Boolean(auth?.isAuthenticated) && currentUser != null && needsBackupEmail;
  const holdLandingForBackup = awaitingBackupCode || showBackupEmail;
  const showLandingAuth = showSignInScreen || holdLandingForBackup;
  const showCompleteAccount =
    Boolean(auth?.isAuthenticated) &&
    currentUser != null &&
    !currentUser.accountComplete &&
    !signupProofPending &&
    !needsBackupEmail &&
    !awaitingBackupCode;
  const needsStudioIntent =
    Boolean(auth?.isAuthenticated) &&
    currentUser != null &&
    Boolean(currentUser.accountComplete) &&
    !currentUser.studioIntentChosen;
  // First run for the single Studio agent (name + colour) used to gate the whole
  // app here, before the shell. It is no longer a gate: the setup now lives in
  // the agent chat's own composer (see StudioAgentSetupComposer), keyed off the
  // agent's DEFAULT identity via studioBots.agentSetupState. The rest of the app
  // stays usable while the owner has not named their agent.
  const showPlanGate =
    choosePlan &&
    !screenshotStep &&
    Boolean(auth?.isAuthenticated) &&
    currentUser != null &&
    Boolean(currentUser.backupEmailVerified) &&
    !showBackupEmail &&
    !awaitingBackupCode;
  const showShell =
    Boolean(auth?.isAuthenticated) &&
    currentUser != null &&
    Boolean(currentUser.accountComplete) &&
    Boolean(currentUser.studioIntentChosen) &&
    !choosePlan &&
    !screenshotStep;

  const setDefaultStudioTab = useMutation(api.users.setDefaultStudioTab);
  const [intentBackfilling, setIntentBackfilling] = useState(false);
  const [intentGateReady, setIntentGateReady] = useState(false);
  const intentHandledRef = useRef(false);

  // Skip the old “What brings you here?” gate — land on Create (composer).
  useEffect(() => {
    if (!needsStudioIntent) {
      intentHandledRef.current = false;
      setIntentGateReady(true);
      return;
    }
    if (intentHandledRef.current) return;
    intentHandledRef.current = true;
    setIntentBackfilling(true);
    const tab = readStoredStudioDefaultTab() ?? "composer";
    writeStoredStudioDefaultTab(tab);
    void setDefaultStudioTab({ tab, markIntentChosen: true })
      .catch(() => {
        intentHandledRef.current = false;
      })
      .finally(() => {
        setIntentBackfilling(false);
        setIntentGateReady(true);
      });
  }, [needsStudioIntent, setDefaultStudioTab]);

  // Drive the layout's static boot frame (StudioBootFrame) across
  // auth → user → shell-chunk. Dismissing it hands over to the real shell, or
  // reveals sign-in / finish-account when the session turned out bad.
  const bootNeeded =
    !screenshotStep &&
    !showPlanGate &&
    !shellFailed &&
    !showLandingAuth &&
    !showCompleteAccount &&
    (authPending ||
      userPending ||
      // Keep the frame (if it is still up) over the dead-session sign-out
      // instead of dismissing to an empty page; sign-in follows once the
      // tokens are cleared.
      deadSession ||
      intentBackfilling ||
      (needsStudioIntent && !intentGateReady) ||
      (showShell && !shellReady));

  useEffect(() => {
    if (!paintBootClaimedRef.current) return;
    if (bootNeeded) claimPaintBoot();
    else dismissPaintBoot();
  }, [bootNeeded]);

  return (
    <>
      <MobileBackStackHost />
      {screenshotStep ? (
        <StudioSignIn screenshotStep={screenshotStep} />
      ) : signinPreview ? (
        <StudioSignIn />
      ) : showLandingAuth ? (
        holdLandingForBackup ? (
          <StudioBackupEmail
            currentUser={currentUser}
            skipCollect={Boolean(signupBackup)}
            externalError={signupBackupError}
          />
        ) : (
          <StudioSignIn
            onAccountCreated={(pending) => {
              pendingSignupRef.current = pending;
              setChoosePlan(true);
              setSignupBackupError("");
              if (pending.emailProof) {
                setSignupBackup("");
                setAwaitingBackupCode(false);
                setSignupProofPending(true);
                return;
              }
              setSignupBackup(pending.backupEmail ?? "");
              setAwaitingBackupCode(true);
            }}
            onAccountCreateFailed={() => {
              pendingSignupRef.current = null;
              completingSignupRef.current = false;
              setChoosePlan(false);
              setSignupProofPending(false);
              setSignupBackup("");
              setSignupBackupError("");
              setAwaitingBackupCode(false);
            }}
          />
        )
      ) : null}
      {showPlanGate ? (
        <AuthFrame title="Choose a plan" wide page>
          <SignupPlanStep
            preview={false}
            onSkip={() => {
              setChoosePlan(false);
              const url = new URL(window.location.href);
              if (url.searchParams.get("test") === "signup-plans") {
                url.searchParams.delete("test");
                window.history.replaceState(null, "", url.toString());
              }
            }}
            onEnterStudio={() => {
              // Session is already live (the plan step only shows when signed in). Leave the plan
              // gate and land on the default view: clean URL, no billing or invoices deep link.
              setChoosePlan(false);
              const url = new URL(window.location.href);
              for (const key of ["test", "open", "tab", "billing"]) url.searchParams.delete(key);
              window.history.replaceState(null, "", url.toString());
              // If the session dropped meanwhile, the gate falls back to the sign-in screen.
            }}
          />
        </AuthFrame>
      ) : null}
      {showCompleteAccount ? <StudioCompleteAccount currentUser={currentUser} /> : null}
      {showShell ? (
        <StudioShellErrorBoundary onFailed={markShellFailed}>
          <DisplayCurrencyProvider>
            <StudioShell
              initialProfileUsername={initialProfileUsername}
              onReady={markShellReady}
            />
          </DisplayCurrencyProvider>
        </StudioShellErrorBoundary>
      ) : null}
    </>
  );
}

function StudioBackupEmail({
  currentUser,
  embedded = false,
  skipCollect = false,
  externalError = "",
}: {
  currentUser?: {
    email?: string;
    backupEmail?: string;
  } | null;
  embedded?: boolean;
  /** New sign-up already collected the backup email, so open on the code step. */
  skipCollect?: boolean;
  externalError?: string;
}) {
  const { signOut } = useAuthActions();
  const requestBackupCode = useAction(api.studioIdentity.requestBackupCode);
  const verifyBackupCode = useAction(api.studioIdentity.verifyBackupCode);
  const setBackupEmail = useMutation(api.studioIdentity.setBackupEmail);
  const backupEmail = currentUser?.backupEmail?.trim() ?? "";
  const collecting = !backupEmail && !skipCollect;
  const [backupInput, setBackupInput] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [pending, setPending] = useState(false);
  const sentRef = useRef(false);

  useEffect(() => {
    if (externalError) setError(externalError);
  }, [externalError]);
  const codeSubmitRef = useRef(false);
  const digits = code.replace(/\D/g, "").slice(0, 6);

  useEffect(() => {
    if (!backupEmail || sentRef.current) return;
    sentRef.current = true;
    void requestBackupCode({}).catch((err: unknown) => {
      sentRef.current = false;
      setError(friendlyConvexError(err, "Could not send a backup code"));
    });
  }, [backupEmail, requestBackupCode]);

  const verifyCode = useCallback(
    (value: string) => {
      if (value.length !== 6 || pending || codeSubmitRef.current) return;
      codeSubmitRef.current = true;
      setPending(true);
      setError("");
      void verifyBackupCode({ code: value })
        .catch((err: unknown) => {
          setError(friendlyConvexError(err, "That code is wrong"));
          setCode("");
          codeSubmitRef.current = false;
        })
        .finally(() => setPending(false));
    },
    [pending, verifyBackupCode],
  );

  useEffect(() => {
    if (digits.length !== 6) {
      codeSubmitRef.current = false;
      return;
    }
    verifyCode(digits);
  }, [digits, verifyCode]);

  return (
    <AuthFrame
      embedded={embedded}
      title={collecting ? "Add a backup email" : "Enter code"}
    >
      {collecting ? (
        <p className="studio-auth-copy m-0 text-center text-[13px]">
          If you lose your password, this email gets you back in. It is not how you sign in.
        </p>
      ) : backupEmail ? (
        <p className="studio-auth-copy m-0 text-center text-[13px]">
          We sent a code to <strong>{backupEmail}</strong>. If you lose your account, this inbox
          gets you back into <strong>{currentUser?.email}</strong>.
        </p>
      ) : (
        <p className="studio-auth-copy m-0 text-center text-[13px]">
          Sending a code to your backup email.
        </p>
      )}
      <form
        className="studio-auth-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (!collecting) return;
          const backup = backupInput.trim().toLowerCase();
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(backup)) {
            setError("Enter a valid backup email");
            return;
          }
          setPending(true);
          setError("");
          void setBackupEmail({ backupEmail: backup })
            .then(() => requestBackupCode({}))
            .then(() => {
              sentRef.current = true;
            })
            .catch((err: unknown) => {
              setError(friendlyConvexError(err, "Could not save your backup email"));
            })
            .finally(() => setPending(false));
        }}
      >
        {collecting ? (
          <label className="block">
            <span className="studio-auth-field is-email">
              <Mail className="studio-auth-accent-text" aria-hidden="true" />
              <input
                value={backupInput}
                onChange={(event) => setBackupInput(event.target.value)}
                placeholder="you@gmail.com"
                aria-label="Backup email"
                type="email"
                autoComplete="email"
                required
                autoFocus
              />
            </span>
          </label>
        ) : (
          <input
            className="studio-auth-field is-code is-email-otp"
            value={digits}
            placeholder="000-000"
            aria-label="Backup email code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            required
            autoFocus
            disabled={pending}
            onChange={(event) => {
              setCode(event.target.value.replace(/\D/g, "").slice(0, 6));
              setError("");
            }}
            onPaste={(event) => {
              const text = event.clipboardData.getData("text");
              const pasted = text.replace(/\D/g, "").slice(0, 6);
              if (!pasted) return;
              event.preventDefault();
              setCode(pasted);
              setError("");
            }}
          />
        )}
        {notice ? <p className="studio-auth-notice">{notice}</p> : null}
        {error ? <p className="studio-auth-error-box">{error}</p> : null}
        {collecting ? (
          <button className="studio-auth-primary" type="submit" disabled={pending || !currentUser}>
            {pending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <>
                Continue
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </>
            )}
          </button>
        ) : null}
        {!collecting && backupEmail ? (
          <button
            type="button"
            className="studio-auth-link w-full cursor-pointer py-1 text-center transition focus:outline-none"
            disabled={pending}
            onClick={() => {
              setPending(true);
              setError("");
              setCode("");
              codeSubmitRef.current = false;
              void requestBackupCode({})
                .then(() => {
                  sentRef.current = true;
                  setNotice("New code sent.");
                })
                .catch((err: unknown) => {
                  setError(friendlyConvexError(err, "Could not resend code"));
                })
                .finally(() => setPending(false));
            }}
          >
            Resend code
          </button>
        ) : null}
        {pending && !collecting ? (
          <p className="studio-auth-notice m-0 flex items-center justify-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            Checking code…
          </p>
        ) : null}
        <button
          type="button"
          className="studio-auth-link w-full cursor-pointer py-1 text-center transition focus:outline-none"
          disabled={pending}
          onClick={() => void signOut()}
        >
          Sign out
        </button>
      </form>
    </AuthFrame>
  );
}

function StudioCompleteAccount({
  currentUser,
}: {
  currentUser: {
    name?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}) {
  const { signOut } = useAuthActions();
  const updateAccountDetails = useMutation(api.users.updateAccountDetails);
  const legacyParts = splitDisplayNameParts(currentUser.name);
  const [firstName, setFirstName] = useState(currentUser.firstName ?? legacyParts.firstName);
  const [lastName, setLastName] = useState(currentUser.lastName ?? legacyParts.lastName);
  const email = currentUser.email?.trim() ?? "";
  const [country, setCountry] = useState("Trinidad and Tobago");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const canContinue =
    firstName.trim().length > 0 && lastName.trim().length > 0 && email.length > 0;

  return (
    <AuthFrame title="Finish your account">
      <p className="studio-auth-copy">
        Your name and country stay on this account. You can change them later in Settings.
      </p>
      <form
        className="studio-auth-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (!firstName.trim() || !lastName.trim()) {
            setError("Enter your first and last name");
            return;
          }
          setPending(true);
          setError("");
          void updateAccountDetails({
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email,
            country,
          })
            .catch((err: unknown) => {
              setError(friendlyConvexError(err, "Could not save account details"));
            })
            .finally(() => setPending(false));
        }}
      >
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          <label className="block">
            <span className="studio-auth-field is-email">
              <UserRound className="studio-auth-accent-text" aria-hidden="true" />
              <input
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                placeholder="First name"
                aria-label="First name"
                autoComplete="given-name"
                required
              />
            </span>
          </label>
          <label className="block">
            <span className="studio-auth-field is-email">
              <UserRound className="studio-auth-accent-text" aria-hidden="true" />
              <input
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                placeholder="Last name"
                aria-label="Last name"
                autoComplete="family-name"
                required
              />
            </span>
          </label>
        </div>
        <label className="block">
          <BillingCountrySelect
            leadingIcon
            value={country}
            onChange={setCountry}
          />
        </label>
        {error ? <p className="studio-auth-error-box">{error}</p> : null}
        <button
          type="submit"
          className="studio-auth-primary"
          disabled={pending || !canContinue}
        >
          {pending ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <>
              Continue
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </>
          )}
        </button>
        <button
          type="button"
          className="studio-auth-link w-full py-1 text-center"
          onClick={() => void signOut()}
        >
          Sign out
        </button>
      </form>
    </AuthFrame>
  );
}

function splitDisplayNameParts(name: string | undefined): { firstName: string; lastName: string } {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return { firstName: "", lastName: "" };
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0] ?? "", lastName: "" };
  return {
    firstName: parts[0] ?? "",
    lastName: parts.slice(1).join(" "),
  };
}

export function AuthPasswordField({
  password,
  onPassword,
  autoComplete = "new-password",
  autoFocus = false,
  placeholder = "Password",
  lengthHint = true,
}: {
  password: string;
  onPassword: (value: string) => void;
  autoComplete?: string;
  autoFocus?: boolean;
  placeholder?: string;
  lengthHint?: boolean;
}) {
  const [visible, setVisible] = useState(false);
  const tooShort = lengthHint && password.length > 0 && password.length < 8;
  return (
    <>
      <label className="block">
        <span className="studio-auth-field is-email is-password">
          <Lock className="studio-auth-accent-text" aria-hidden="true" />
          <input
            value={password}
            onChange={(event) => onPassword(event.target.value)}
            placeholder={placeholder}
            aria-label={placeholder}
            type={visible ? "text" : "password"}
            size={1}
            autoComplete={autoComplete}
            required
            autoFocus={autoFocus}
          />
          <button
            type="button"
            className="studio-auth-password-visibility"
            aria-label={visible ? "Hide password" : "Show password"}
            aria-pressed={visible}
            onClick={() => setVisible((on) => !on)}
          >
            {visible ? (
              <EyeOff className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Eye className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        </span>
      </label>
      {tooShort ? (
        <p className="studio-auth-hint is-taken">At least 8 characters</p>
      ) : null}
    </>
  );
}

type SignupPlanRow = {
  _id: string;
  name: string;
  slug: string;
  monthlyPriceCents: number;
  originalMonthlyPriceCents?: number;
  discountPercent?: number;
  annualDiscountPercent?: number;
  sortOrder: number;
};

function SignupPlanStep({
  preview,
  onSkip,
  onEnterStudio,
}: {
  preview: boolean;
  onSkip: () => void;
  /** Paid during sign-up: "Sign in" on Payment confirmed lands in Studio on the default view. */
  onEnterStudio?: () => void;
}) {
  const catalog = useQuery(api.billing.listSubscriptionPlans, {});
  // The plan table is seeded lazily from STUDIO_PLAN_CATALOG (same as the billing pane).
  // A fresh database (preview, or a brand-new account's first visit) can have no rows yet.
  const ensureStudioPlans = useMutation(api.billing.ensureStudioPlans);
  const [seedState, setSeedState] = useState<"idle" | "seeding" | "done" | "failed">("idle");
  useEffect(() => {
    if (preview || catalog === undefined || catalog.length > 0 || seedState !== "idle") return;
    setSeedState("seeding");
    let attempts = 0;
    const run = () => {
      attempts += 1;
      ensureStudioPlans({})
        .then(() => setSeedState("done"))
        .catch(() => {
          // Right after sign-up the session can lag a moment; retry briefly.
          if (attempts < 5) window.setTimeout(run, 800);
          else setSeedState("failed");
        });
    };
    run();
  }, [preview, catalog, seedState, ensureStudioPlans]);
  const billingRail = useQuery(api.users.getBillingRail, preview ? "skip" : {});
  const contactEmail = useCheckoutContactEmail(preview);
  const { currency, setCurrency, fxUsdToTtd, fxApproximate, ttdAllowed } = useCheckoutCurrency(
    preview ? null : billingRail?.country,
  );
  const startSubscribe = useAction(api.wamActions.startSubscribe);
  const verifyPlayPurchase = useAction(api.playBillingActions.verifyPlayPurchase);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(preview ? "Preview only. No payment is sent." : "");
  const seededPlan = useRef(false);

  const plans = useMemo(() => {
    const live = [...(catalog ?? [])]
      .filter((plan) => STUDIO_PLAN_SLUGS.includes(plan.slug as (typeof STUDIO_PLAN_SLUGS)[number]))
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .slice(0, 3);
    if (live.length > 0) return live;
    if (!preview) return [];
    return STUDIO_PLAN_CATALOG.map((plan) => ({
      _id: plan.slug,
      name: plan.name,
      slug: plan.slug,
      monthlyPriceCents: plan.faceMonthlyCents,
      originalMonthlyPriceCents: plan.faceMonthlyCents,
      discountPercent: plan.monthlyDiscountPercent,
      annualDiscountPercent: plan.annualDiscountPercent,
      sortOrder: plan.sortOrder,
    }));
  }, [catalog, preview]);
  const [interval, setPlanInterval] = useState<"month" | "year">("month");
  const maxAnnualSave = Math.max(...STUDIO_PLAN_CATALOG.map((plan) => plan.annualDiscountPercent));
  const selected = plans.find((plan) => plan._id === selectedId) ?? null;
  const charge = selected ? quoteBillingPlan(selected, interval).chargeCents : 0;
  const checkoutPay = useCheckoutPayment({
    preview,
    currency,
    paypalDisabled: shouldUsePlayBilling(),
    requestPrefix: "sub",
    requestKey: `${selected?._id ?? ""}:${interval}`,
    confirmedDetail: (amount) => planConfirmedDetail(selected?.name, interval, amount),
    confirmedAction: "signin",
    order: () =>
      selected
        ? { kind: "subscribe", planId: selected._id as never, interval }
        : "Choose a plan.",
    startTtd: async (clientRequestId) => {
      if (!selected) return;
      const result = await startSubscribe({
        clientRequestId,
        planId: selected._id as never,
        interval,
        currency: "TTD",
      });
      // Wam returns to /pay/done; the flag makes "Payment received" read "Sign in" and stay on the default view.
      stashWamReturn({ signup: true, paymentId: String(result.paymentId), billing: "plans" });
      window.location.assign(result.checkoutUrl);
    },
    onPaid: () => {
      setStatus("PayPal payment received. Plan is active.");
      (onEnterStudio ?? onSkip)();
    },
    onMessage: (message) => setStatus(message),
  });

  useEffect(() => {
    if (seededPlan.current || !preview || selectedId || plans.length === 0) return;
    const slug = signupScreenshotPlan(window.location.hostname, window.location.search);
    if (!slug) return;
    const match = plans.find((plan) => plan.slug === slug);
    if (!match) return;
    seededPlan.current = true;
    setSelectedId(match._id);
  }, [preview, plans, selectedId]);

  async function pay(plan: SignupPlanRow) {
    if (preview) {
      setStatus("Preview only. No payment is sent.");
      return;
    }
    setBusy(true);
    setStatus("");
    try {
      if (shouldUsePlayBilling()) {
        const sku = playSubscriptionProduct(plan.slug, interval);
        if (!sku) {
          setStatus("This plan is not available in the app.");
          return;
        }
        const tx = await startPlayPurchase({
          productId: sku.productId,
          productType: "subs",
          basePlanId: sku.basePlanId,
        });
        const result = await verifyPlayPurchase(tx);
        setStatus(playGrantMessage(result));
        return;
      }
      await checkoutPay.payTtd();
    } catch (error) {
      setStatus(friendlyConvexError(error, "Could not start checkout."));
    } finally {
      setBusy(false);
    }
  }

  const plansLoading = !preview && plans.length === 0 && seedState !== "failed";
  const skip = (
    <button type="button" className="studio-plan-later" onClick={onSkip}>
      <span>Pay later</span>
      <ArrowRight size={14} strokeWidth={2.25} aria-hidden="true" />
    </button>
  );
  const planCards = (
    <StudioPlanCards stack beforeTrust={<div className="studio-plan-later-row">{skip}</div>}>
      {plans.map((plan) => (
        <StudioPlanCard
          usd={currency === "USD"}
          key={plan._id}
          plan={plan}
          interval={interval}
          quote={quoteBillingPlan(plan, interval)}
          featured={plan.slug === "plus"}
        >
          <button
            type="button"
            className={`studio-billing-card-cta${plan.slug === "plus" ? " is-featured-cta" : ""}`}
            onClick={() => {
              setSelectedId(plan._id);
              setStatus(preview ? "Preview only. No payment is sent." : "");
            }}
          >
            <span>Subscribe</span>
            <ArrowRight aria-hidden="true" />
          </button>
        </StudioPlanCard>
      ))}
    </StudioPlanCards>
  );
  if (selected) {
    const lockedRate = checkoutPay.lockedFxUsdToTtd;
    const shownRate = lockedRate ?? fxUsdToTtd ?? (preview ? 7 : null);
    return (
      <UnifiedCheckout
        ttdAllowed={ttdAllowed}
        viewport
        preview={preview}
        intent="subscribe"
        period={interval === "year" ? "per year" : "per month"}
        title={selected.name}
        email={preview ? SIGNUP_SCREENSHOT_EMAIL : contactEmail}
        lines={[
          { label: selected.name, detail: `${interval === "year" ? "Annual" : "Monthly"} plan. ${planPitch(selected.slug)}`, ttdCents: charge },
        ]}
        totalTtdCents={charge}
        fxUsdToTtd={shownRate}
        fxApproximate={lockedRate == null && (fxUsdToTtd == null ? preview : fxApproximate)}
        currency={currency}
        onCurrencyChange={(next) => {
          setCurrency(next);
        }}
        busy={busy || checkoutPay.busy}
        status={status}
        onPay={() => void pay(selected)}
        onBack={() => setSelectedId(null)}
        paypal={checkoutPay.paypal}
      />
    );
  }
  return (
    <div className="studio-auth-plans">
      {plansLoading ? (
        <div className="studio-auth-plans-loading" role="status" aria-live="polite">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          <span>Loading plans</span>
        </div>
      ) : null}
      {!preview && plans.length === 0 && seedState === "failed" ? (
        <p className="studio-auth-field-help">Plans could not load. You can skip and choose one later in Billing.</p>
      ) : null}
      <div className="studio-plan-topbar">
        <div className="studio-plan-brand"><span className="uc2-logo-mark"><SanityMark height={18} className="uc2-sanity-mark" title="Sanity" /><span className="uc2-wordmark">Yatishara Studio</span></span></div>
        <StudioPlanBarControls interval={interval} onChange={setPlanInterval} maxSave={maxAnnualSave} currency={currency} onCurrencyChange={setCurrency} />
      </div>
      <StudioChoosePlanHead interval={interval} onChange={setPlanInterval} maxSave={maxAnnualSave} hideSwitches />
      {planCards}
    </div>
  );
}

function StudioSignIn({
  onBack,
  embedded = false,
  onAccountCreated,
  onAccountCreateFailed,
  screenshotStep,
}: {
  onBack?: () => void;
  /** When true, fill landing content stage (chrome stays). */
  embedded?: boolean;
  onAccountCreated?: (pending: PendingSignup) => void;
  onAccountCreateFailed?: () => void;
  /** Preview/localhost screenshot. Never sends a code or creates an account. */
  screenshotStep?: SignupStep;
} = {}) {
  const screenshot = screenshotStep != null;
  const { signIn } = useAuthActions();
  // Continue with Google: same server flow as the Sanity web sign-up (GIS ID token, WEB client id,
  // sanityWebSignup.googleStart / finishGoogleSignup, Convex Auth "sanity-google" provider).
  const googleClientId = useQuery(api.sanityWebSignup.googleClientId, screenshot ? "skip" : {});
  const googleStart = useAction(api.sanityWebSignup.googleStart);
  const finishGoogleSignup = useAction(api.sanityWebSignup.finishGoogleSignup);
  const [google, setGoogle] = useState<{ idToken: string; email: string; proof: string } | null>(null);
  const convex = useConvex();
  const requestSignupEmailCode = useAction(api.studioIdentity.requestSignupEmailCode);
  const verifySignupEmailCode = useAction(api.studioIdentity.verifySignupEmailCode);
  const [step, setStep] = useState<"signin" | "signup">(screenshot ? "signup" : "signin");
  const signinSubmitRef = useRef<(() => void) | null>(null);
  const [signupStep, setSignupStep] = useState<SignupStep>(screenshotStep ?? 1);
  const [emailInput, setEmailInput] = useState("");
  const [firstName, setFirstName] = useState(screenshot ? SIGNUP_SCREENSHOT_NAME.firstName : "");
  const [lastName, setLastName] = useState(screenshot ? SIGNUP_SCREENSHOT_NAME.lastName : "");
  const [handleTouched, setHandleTouched] = useState(false);
  const [handleDraft, setHandleDraft] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [confirmInput, setConfirmInput] = useState("");
  const [backupInput, setBackupInput] = useState(screenshot ? SIGNUP_SCREENSHOT_EMAIL : "");
  const screenshotConfirmed =
    screenshot &&
    screenshotStep === 2 &&
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("signupConfirmed") === "1";
  const [codeSent, setCodeSent] = useState(screenshot && screenshotStep === 2 && !screenshotConfirmed);
  const [code, setCode] = useState("");
  const [emailProof, setEmailProof] = useState(
    (screenshot && screenshotStep != null && screenshotStep >= 3) || screenshotConfirmed ? "screenshot" : "",
  );
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const codeSubmitRef = useRef(false);

  const isSignup = step === "signup";
  // Address is derived from the name only; it cannot be edited.
  // Both names must be present. A first name alone used to suggest "dallas2".
  const handleManual = false;
  void handleTouched;
  const nameReady = Boolean(firstName.trim() && lastName.trim());
  const suggestion = useQuery(
    api.studioIdentity.suggestSignupHandle,
    !screenshot && isSignup && !handleManual && nameReady
      ? { firstName: firstName.trim(), lastName: lastName.trim() }
      : "skip",
  );
  const suggestedLocal = suggestion?.ok ? suggestion.localPart : "";
  const handleShown = handleManual ? handleDraft : suggestedLocal;
  const liveLocal = normalizeSignupHandle(handleShown);
  const [debouncedLocal, setDebouncedLocal] = useState("");
  useEffect(() => {
    if (screenshot) return;
    const timer = window.setTimeout(() => setDebouncedLocal(liveLocal), SIGNUP_HANDLE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [liveLocal, screenshot]);
  const availability = useQuery(
    api.studioIdentity.handleAvailable,
    !screenshot && isSignup && debouncedLocal.length >= 3 ? { localPart: debouncedLocal } : "skip",
  );
  const availabilityCurrent =
    availability != null && availability.localPart === debouncedLocal ? availability : undefined;
  const handleChecking =
    !screenshot &&
    liveLocal.length >= 3 &&
    (liveLocal !== debouncedLocal || availabilityCurrent === undefined);
  const addressOk = screenshot
    ? liveLocal.length >= 3
    : liveLocal.length >= 3 && !handleChecking && availabilityCurrent?.ok === true;
  const handleStatus = signupHandleMessage({
    localPart: liveLocal,
    checking: handleChecking,
    availability:
      screenshot && liveLocal.length >= 3 ? { ok: true } : (availabilityCurrent ?? null),
  });
  const handleState: "idle" | "checking" | "ok" | "bad" = !liveLocal
    ? "idle"
    : handleStatus === "Checking"
      ? "checking"
      : handleStatus === "Available"
        ? "ok"
        : handleStatus
          ? "bad"
          : "idle";
  const backup = backupInput.trim().toLowerCase();
  const backupReady = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(backup);
  const codeDigits = code.replace(/\D/g, "").slice(0, 6);
  const continueReady = canContinueSignup({
    step: signupStep,
    firstName,
    lastName,
    addressOk,
    emailProof,
    password: passwordInput,
    confirm: confirmInput,
  });
  const mismatch = passwordMismatchMessage(passwordInput, confirmInput);

  const googleBlock = (
          <div className="studio-auth-google">
            <GoogleButton
              clientId={googleClientId}
              preview={screenshot}
              pending={pending}
              onError={setError}
              onCredential={(idToken) => {
                setPending(true);
                setError("");
                void googleStart({ idToken })
                  .then(async (r) => {
                    if (r.next === "signin") {
                      await signIn("sanity-google", { idToken });
                      return;
                    }
                    if (!r.emailProof) throw new Error("That Google email is already used by an account. Sign in instead.");
                    setGoogle({ idToken, email: r.email, proof: r.emailProof });
                    if (!firstName.trim()) setFirstName(r.givenName);
                    if (!lastName.trim()) setLastName(r.familyName);
                    setStep("signup");
                    setSignupStep(1);
                  })
                  .catch((err: unknown) => setError(friendlyConvexError(err, "Google sign-in failed. Try again")))
                  .finally(() => setPending(false));
              }}
            />
          </div>
  );

  const resetToSignIn = () => {
    setError("");
    setPasswordInput("");
    setConfirmInput("");
    setHandleTouched(false);
    setHandleDraft("");
    setSignupStep(1);
    setCodeSent(false);
    setCode("");
    setEmailProof("");
    setStep("signin");
  };

  const verifyCode = useCallback(
    (value: string) => {
      if (screenshot || value.length !== 6 || pending || codeSubmitRef.current || !backupReady) return;
      codeSubmitRef.current = true;
      setPending(true);
      setError("");
      void verifySignupEmailCode({ email: backup, code: value })
        .then((row) => {
          setEmailProof(row.proof);
          setCodeSent(false);
          setCode("");
        })
        .catch((err: unknown) => {
          setError(friendlyConvexError(err, "That code is wrong"));
          setCode("");
          codeSubmitRef.current = false;
        })
        .finally(() => setPending(false));
    },
    [backup, backupReady, pending, screenshot, verifySignupEmailCode],
  );

  useEffect(() => {
    if (!isSignup || signupStep !== 2 || !codeSent) return;
    if (codeDigits.length !== 6) {
      codeSubmitRef.current = false;
      return;
    }
    if (screenshot) {
      setEmailProof("screenshot");
      setCodeSent(false);
      setCode("");
      return;
    }
    verifyCode(codeDigits);
  }, [codeDigits, codeSent, isSignup, screenshot, signupStep, verifyCode]);

  const signupTitle =
    signupStep === 1
      ? "Your name"
      : signupStep === 2
        ? codeSent
          ? "Enter code"
          : "Backup email"
        : signupStep === 3
          ? "Choose a password"
          : "Choose a plan";

  return (
    <AuthFrame
      embedded={embedded}
      wide={isSignup && signupStep === 4}
      page={isSignup && signupStep === 4}
      title={isSignup ? signupTitle : "Yatishara Studio"}
      progress={isSignup ? signupProgress(signupStep) : undefined}
      onBack={
        isSignup && (signupStep === 2 || signupStep === 3)
          ? () => {
              if (pending) return;
              setError("");
              setSignupStep((current) => (current - 1) as SignupStep);
            }
          : embedded || !onBack
            ? undefined
            : onBack
      }
    >
      <form
        className="studio-auth-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (step === "signin") {
            signinSubmitRef.current?.();
            return;
          }
          setPending(true);
          setError("");

          if (signupStep === 1) {
            if (!firstName.trim() || !lastName.trim()) {
              setError("Enter your first and last name");
              setPending(false);
              return;
            }
            if (google) {
              // Google-verified email is the backup; no password (can be added later).
              const g = google;
              void finishGoogleSignup({
                idToken: g.idToken,
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                emailProof: g.proof,
              })
                .then(() => signIn("sanity-google", { idToken: g.idToken }))
                .then(() => setSignupStep(4))
                .catch((err: unknown) => {
                  if (/expired|failed/i.test(String(err))) setGoogle(null);
                  setError(friendlyConvexError(err, "Google sign-in expired. Continue with Google again"));
                })
                .finally(() => setPending(false));
              return;
            }
            if (!addressOk) {
              setError(
                availabilityCurrent?.reason === "taken"
                  ? "That address is already taken. Sign in instead."
                  : availabilityCurrent?.reason === "reserved"
                    ? "That address is reserved. Pick another."
                    : handleStatus || "That address isn't valid.",
              );
              setPending(false);
              return;
            }
            setSignupStep(2);
            setPending(false);
            return;
          }
          if (signupStep === 2) {
            if (emailProof) {
              setSignupStep(3);
              setPending(false);
              return;
            }
            if (codeSent) {
              setPending(false);
              return;
            }
            if (!backupReady) {
              setError("Enter a valid email");
              setPending(false);
              return;
            }
            if (screenshot) {
              setCodeSent(true);
              setCode("");
              setPending(false);
              return;
            }
            void requestSignupEmailCode({ email: backup })
              .then((row) => {
                if (!row.sent && codeSent) {
                  setError("Wait a moment, then send another code");
                  return;
                }
                setCodeSent(true);
                setCode("");
              })
              .catch((err: unknown) => {
                setError(friendlyConvexError(err, "Could not send a code"));
              })
              .finally(() => setPending(false));
            return;
          }
          if (signupStep !== 3) {
            setPending(false);
            return;
          }
          if (screenshot) {
            setSignupStep(4);
            setPending(false);
            return;
          }
          if (!continueReady) {
            setError(mismatch || "Password must be at least 8 characters");
            setPending(false);
            return;
          }
          if (!backupReady || !emailProof || emailProof === "screenshot") {
            setError("Confirm your email again");
            setSignupStep(2);
            setPending(false);
            return;
          }
          const given = firstName.trim();
          const family = lastName.trim();
          const chosen = normalizeSignupHandle(handleShown);
          void convex
            .query(api.studioIdentity.handleAvailable, { localPart: chosen })
            .then((row) => {
              if (!row.ok || !row.localPart || !row.address) {
                setError(
                  row.reason === "taken"
                    ? "That address is already taken. Sign in instead."
                    : row.reason === "reserved"
                      ? "That address is reserved. Pick another."
                      : "That address isn't valid.",
                );
                return;
              }
              if (backup === row.address.trim().toLowerCase()) {
                setError("Backup email must be a different inbox");
                return;
              }
              const name = `${given} ${family}`;
              onAccountCreated?.({
                localPart: row.localPart,
                firstName: given,
                lastName: family,
                backupEmail: backup,
                emailProof,
                auto: true,
              });
              return signIn("password", {
                flow: "signUp",
                email: row.address,
                password: passwordInput,
                name,
              }).catch((err: unknown) => {
                const message = err instanceof Error ? err.message : String(err);
                if (!/already exists/i.test(message)) throw err;
                onAccountCreateFailed?.();
                return signIn("password", {
                  flow: "signIn",
                  email: row.address,
                  password: passwordInput,
                });
              });
            })
            .catch((err: unknown) => {
              onAccountCreateFailed?.();
              setError(friendlyConvexError(err, "Could not create your account"));
            })
            .finally(() => setPending(false));
        }}
      >
        <StudioHeadTabs
          className="studio-auth-mode-tabs"
          aria-label="Sign in or sign up"
          disabled={pending}
          tabs={[
            {
              id: "signin",
              label: "Sign in",
              icon: <LogIn size={14} strokeWidth={2.25} aria-hidden="true" />,
            },
            {
              id: "signup",
              label: "Sign up",
              icon: <UserPlus size={14} strokeWidth={2.25} aria-hidden="true" />,
            },
          ]}
          activeId={step}
          onChange={(id) => {
            if (id === "signin") {
              resetToSignIn();
              return;
            }
            setError("");
            if (step === "signup") return;
            setPasswordInput("");
            setConfirmInput("");
            setSignupStep(1);
            setStep("signup");
          }}
        />
        {isSignup && signupStep < 4 ? (
          <div className="grid grid-cols-1 gap-2.5">
            {signupStep === 1 ? (
              <>
                <label className="block">
                  <span className="studio-auth-field is-email">
                    <UserRound className="studio-auth-accent-text" aria-hidden="true" />
                    <input
                      value={firstName}
                      onChange={(event) => setFirstName(event.target.value)}
                      placeholder="First name"
                      aria-label="First name"
                      autoComplete="given-name"
                      required
                      autoFocus
                    />
                  </span>
                </label>
                <label className="block">
                  <span className="studio-auth-field is-email">
                    <UserRound className="studio-auth-accent-text" aria-hidden="true" />
                    <input
                      value={lastName}
                      onChange={(event) => setLastName(event.target.value)}
                      placeholder="Last name"
                      aria-label="Last name"
                      autoComplete="family-name"
                      required
                    />
                  </span>
                </label>
                <label className="studio-auth-handle block">
                  <span className="studio-auth-field is-email is-handle is-derived">
                    <span
                      className={`studio-auth-handle-icon${
                        handleState === "ok" ? " is-ok" : handleState === "bad" ? " is-bad" : ""
                      }`}
                      aria-hidden="true"
                      title={handleState === "bad" ? handleStatus : undefined}
                    >
                      {handleState === "checking" ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : handleState === "ok" ? (
                        <CheckIcon className="h-3 w-3" strokeWidth={3} />
                      ) : handleState === "bad" ? (
                        <XIcon className="h-3 w-3" strokeWidth={3} />
                      ) : (
                        <AtSign className="studio-auth-accent-text h-4 w-4" />
                      )}
                    </span>
                    <span
                      className={`studio-auth-address-value${handleShown ? "" : " is-empty"}`}
                      aria-label="Your Yatishara address, made from your name"
                    >
                      {handleShown ? `${handleShown}@${DEFAULT_MAIL_DOMAIN}` : `your.name@${DEFAULT_MAIL_DOMAIN}`}
                    </span>
                  </span>
                </label>
                <span className="sr-only" aria-live="polite">
                  {handleState === "bad" ? handleStatus : handleState === "ok" ? "Address available" : ""}
                </span>
              </>
            ) : null}
            {signupStep === 2 ? (
              <>
                <label className="block">
                  <span className="studio-auth-field is-email">
                    <Mail className="studio-auth-accent-text" aria-hidden="true" />
                    <input
                      value={backupInput}
                      onChange={(event) => {
                        setBackupInput(event.target.value);
                        setEmailProof("");
                        setCodeSent(false);
                        setCode("");
                      }}
                      placeholder="Personal email"
                      aria-label="Your personal email (for account recovery)"
                      type="email"
                      autoComplete="email"
                      required
                      autoFocus={!codeSent}
                      readOnly={codeSent || Boolean(emailProof)}
                      aria-describedby={codeSent || emailProof ? "signup-backup-help" : undefined}
                    />
                    {emailProof ? (
                      <span className="studio-auth-handle-icon is-ok" aria-hidden="true">
                        <CheckIcon className="h-3 w-3" strokeWidth={3} />
                      </span>
                    ) : null}
                  </span>
                </label>
                {codeSent ? (
                  <p id="signup-backup-help" className="studio-auth-field-help">
                    {`Enter the 6-digit code we sent to ${backup}.`}
                  </p>
                ) : emailProof ? (
                  <p id="signup-backup-help" className="studio-auth-field-help is-status">
                    Email confirmed.
                  </p>
                ) : null}
                {codeSent ? (
                  <input
                    className="studio-auth-field is-code is-email-otp"
                    value={codeDigits}
                    placeholder="000-000"
                    aria-label="Email code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    required
                    autoFocus
                    disabled={pending}
                    onChange={(event) => {
                      setCode(event.target.value.replace(/\D/g, "").slice(0, 6));
                      setError("");
                    }}
                  />
                ) : null}
                {codeSent && !emailProof ? (
                  <button
                    type="button"
                    className="studio-auth-link w-full cursor-pointer py-1 text-center transition focus:outline-none"
                    disabled={pending}
                    onClick={() => {
                      setEmailProof("");
                      setCodeSent(false);
                      setCode("");
                      setError("");
                    }}
                  >
                    Use a different email
                  </button>
                ) : null}
              </>
            ) : null}
            {signupStep === 3 ? (
              <>
                <AuthPasswordField
                  password={passwordInput}
                  onPassword={setPasswordInput}
                  autoComplete="new-password"
                  autoFocus
                />
                <AuthPasswordField
                  placeholder="Confirm password"
                  password={confirmInput}
                  onPassword={setConfirmInput}
                  autoComplete="new-password"
                  lengthHint={false}
                />
                {mismatch ? <p className="studio-auth-hint is-taken">{mismatch}</p> : null}
              </>
            ) : null}
          </div>
        ) : isSignup ? null : (
          <EmailFirstSignIn submitRef={signinSubmitRef} domain={DEFAULT_MAIL_DOMAIN} afterPrimary={googleBlock} />
        )}

        {error && isSignup ? <p className="studio-auth-error-box">{error}</p> : null}
        {isSignup && signupStep === 2 && codeSent ? (
          <button
            type="button"
            className="studio-auth-link w-full cursor-pointer py-1 text-center transition focus:outline-none"
            disabled={pending}
            onClick={() => {
              if (screenshot) {
                setCode("");
                codeSubmitRef.current = false;
                return;
              }
              setPending(true);
              setError("");
              void requestSignupEmailCode({ email: backup })
                .then((row) => {
                  if (!row.sent) {
                    setError("Wait a moment, then send another code");
                    return;
                  }
                  setCode("");
                  codeSubmitRef.current = false;
                })
                .catch((err: unknown) => {
                  setError(friendlyConvexError(err, "Could not resend code"));
                })
                .finally(() => setPending(false));
            }}
          >
            Resend code
          </button>
        ) : null}
        {pending && isSignup && signupStep === 2 && codeSent && codeDigits.length === 6 ? (
          <p className="studio-auth-notice m-0 flex items-center justify-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            Checking code…
          </p>
        ) : null}
        {!isSignup || signupStep === 4 || (signupStep === 2 && codeSent) ? null : (
        <button
          className={`studio-auth-primary${isSignup ? " has-step-progress" : ""}`}
          type="submit"
          aria-describedby={isSignup ? "studio-signup-step-progress" : undefined}
          style={
            isSignup
              ? ({ "--p": `${Math.round(signupProgress(signupStep) * 100)}%` } as CSSProperties)
              : undefined
          }
          disabled={
            pending ||
            (isSignup && signupStep === 1 && !continueReady) ||
            (isSignup && signupStep === 2 && !emailProof && !backupReady) ||
            (isSignup && signupStep === 2 && Boolean(emailProof) && !continueReady) ||
            (isSignup && signupStep === 3 && !continueReady)
          }
        >
          {isSignup ? (
            <>
              {/* Step progress as the button fill, like an app-store install button. */}
              <span className="studio-auth-primary-fill" aria-hidden="true" />
              <span id="studio-signup-step-progress" className="sr-only">{`Step ${signupStep} of 4`}</span>
            </>
          ) : null}
          <span className="studio-auth-primary-label">
            {pending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : isSignup && signupStep === 2 && !emailProof ? (
              "Send code"
            ) : (
              <>
                Continue
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </>
            )}
          </span>
          {isSignup ? (
            <span className="studio-auth-primary-label is-on-fill" aria-hidden="true">
              {pending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : isSignup && signupStep === 2 && !emailProof ? (
                "Send code"
              ) : (
                <>
                  Continue
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </>
              )}
            </span>
          ) : null}
        </button>
        )}
        {isSignup && signupStep === 1 && !google ? googleBlock : null}
        {isSignup && signupStep === 1 && google ? (
          <button
            type="button"
            className="studio-auth-link w-full cursor-pointer py-1 text-center"
            onClick={() => {
              setGoogle(null);
              setError("");
            }}
          >
            {`Signed in with Google as ${google.email}. Use email instead`}
          </button>
        ) : null}
      </form>
      {isSignup && signupStep === 4 ? (
        <SignupPlanStep
          preview={screenshot}
          onSkip={() => {
            if (screenshot) return;
          }}
        />
      ) : null}
    </AuthFrame>
  );
}

export function AuthFrame({
  title,
  children,
  onBack,
  embedded = false,
  wide = false,
  page = false,
}: {
  title: string;
  /** Full-width page layout (sign-up plans): no narrow card, cards side by side. */
  page?: boolean;
  children?: ReactNode;
  onBack?: () => void;
  /** Fill parent stage instead of owning the full viewport. */
  embedded?: boolean;
  /** Wider sheet for multi-line choice lists. */
  wide?: boolean;
  /** 0 to 1. Draws the slim sign-up progress bar. */
  progress?: number;
}) {
  const wrapperRef = useRef<HTMLElement | null>(null);
  const Wrapper = embedded ? "div" : "main";
  // Seeded to match the server. Date, local time, and localStorage in the
  // first render disagree with the server HTML.
  const [cycleDelay, setCycleDelay] = useState(0);
  const [greeting, setGreeting] = useState<ReturnType<typeof skeletonGreeting> | null>(null);
  const [customCursorEnabled, setCustomCursorEnabled] = useState(true);
  useEffect(() => {
    setCycleDelay(-(Date.now() % AUTH_CYCLE_MS));
    setGreeting(skeletonGreeting());
    setCustomCursorEnabled(readStudioCustomCursorEnabled());
  }, []);
  useStudioCustomCursor(!embedded && customCursorEnabled);
  // Tap anywhere outside the form (placeholder chrome included): flash the
  // card's edge in the accent colour and say "Sign in first". The counter
  // alternates the flash class so every tap restarts it, and keys the toast.
  const [nudge, setNudge] = useState(0);
  const nudgeTimerRef = useRef(0);
  useEffect(() => () => window.clearTimeout(nudgeTimerRef.current), []);
  const triggerSignInNudge = () => {
    setNudge((n) => n + 1);
    window.clearTimeout(nudgeTimerRef.current);
    nudgeTimerRef.current = window.setTimeout(() => setNudge(0), 1700);
  };

  const onAuthStagePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    const target = event.target as Element | null;
    if (!target || target.closest(".studio-auth-card, .studio-auth-page, .unified-checkout")) return;

    if (target.closest(".ysbf-auth-greet, .ysbf-mnav")) {
      triggerSignInNudge();
      return;
    }

    playUiSound("shuffle");
    void nativeImpact("light");
    randomizeStudioAppearance();
  };

  // Mobile: keep the form above the OS keyboard (and landing bottom nav).
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const host =
      (wrapper.closest(".studio-landing") as HTMLElement | null) ?? wrapper;

    let rafId = 0;
    let lastInset = -1;
    let scrollTimer = 0;

    const isMobile = () => window.matchMedia("(max-width: 899px)").matches;

    const syncKeyboardInset = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        const vv = window.visualViewport;
        let inset = 0;
        if (isMobile() && vv) {
          const raw = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
          inset = raw < 6 ? 0 : Math.round(raw);
        }
        if (inset === lastInset) return;
        lastInset = inset;
        host.style.setProperty("--studio-auth-keyboard-inset", `${inset}px`);
        if (inset > 0) host.setAttribute("data-keyboard-open", "1");
        else host.removeAttribute("data-keyboard-open");
      });
    };

    const scrollFocusedIntoView = () => {
      if (!isMobile()) return;
      const active = document.activeElement;
      if (!(active instanceof HTMLElement) || !wrapper.contains(active)) return;
      if (scrollTimer) window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(() => {
        scrollTimer = 0;
        const submit = wrapper.querySelector<HTMLElement>(".studio-auth-primary");
        const target =
          active.closest(".studio-auth-field") ?? active;
        target.scrollIntoView({
          block: "center",
          inline: "nearest",
          behavior: "smooth",
        });
        if (submit) {
          window.setTimeout(() => {
            submit.scrollIntoView({
              block: "nearest",
              inline: "nearest",
              behavior: "smooth",
            });
          }, 80);
        }
      }, 140);
    };

    syncKeyboardInset();
    window.visualViewport?.addEventListener("resize", syncKeyboardInset);
    window.visualViewport?.addEventListener("scroll", syncKeyboardInset);
    window.addEventListener("resize", syncKeyboardInset);
    wrapper.addEventListener("focusin", scrollFocusedIntoView);

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      if (scrollTimer) window.clearTimeout(scrollTimer);
      window.visualViewport?.removeEventListener("resize", syncKeyboardInset);
      window.visualViewport?.removeEventListener("scroll", syncKeyboardInset);
      window.removeEventListener("resize", syncKeyboardInset);
      wrapper.removeEventListener("focusin", scrollFocusedIntoView);
      host.style.removeProperty("--studio-auth-keyboard-inset");
      host.removeAttribute("data-keyboard-open");
    };
  }, []);

  const sheet = (
    <Wrapper
      ref={(node) => {
        wrapperRef.current = node;
      }}
      className={
        embedded
          ? "studio-auth-theme is-live-theme studio-auth-embedded relative flex h-full min-h-0 w-full flex-1 items-center justify-center overflow-auto px-4 py-6 sm:px-5 sm:py-8"
          : "studio-auth-theme is-live-theme ysbf-auth-slot"
      }
    >
      <section
        className={`${page ? "studio-auth-page" : "studio-auth-card"} relative w-full text-center${
          wide && !page ? " is-wide" : ""
        }${nudge > 0 ? (nudge % 2 ? " is-nudge-a" : " is-nudge-b") : ""}`}
      >
        <div className="studio-auth-card-head">
          {onBack ? (
            <button type="button" className="studio-auth-back" aria-label="Back" title="Back" onClick={onBack}>
              <ArrowLeft aria-hidden="true" />
            </button>
          ) : null}
          {page ? null : (
            <div className="flex flex-col items-center justify-center">
              <SanityMark height={34} className="studio-auth-sanity-mark" title="Sanity" />
            </div>
          )}
          <div className={page ? "mt-1" : "mt-3"}>
            <h1 className="studio-auth-title">{title}</h1>
          </div>
        </div>
        <div className="studio-auth-card-body">{children}</div>
      </section>
    </Wrapper>
  );
  if (embedded) return sheet;
  // Standalone sign-in / sign-up / finish-account: the app's own skeleton
  // chrome (the same markup as the boot frame) stays static behind the form,
  // which sits where the DM empty state normally is (mobile: between the
  // header and the bottom nav, inset like the app's cards).
  return (
    <div
      className={`ys-auth-stage${customCursorEnabled ? " is-custom-cursor" : ""}`}
      style={{ "--ysbf-cycle-delay": `${cycleDelay}ms` } as CSSProperties}
      onPointerDown={onAuthStagePointerDown}
    >
      <StudioSkeletonCard auth greeting={greeting ?? undefined} main={sheet} />
      {nudge > 0 ? (
        <div key={nudge} className="ysbf-auth-toast" role="status">
          Sign in first
        </div>
      ) : null}
    </div>
  );
}
